import { FC } from 'react';
import { Card, HStack, Text, Box } from '@wegroup/design-system';
import TableCheckbox from '@parties/src/features/overview/components/table/TableCheckbox';
import { useAnvaFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/anvaFlowsConfigureStore';
import { CompanyWithStatus } from '@flows/src/components/flows-configure/FlowsConfigureCompanies';

interface Props {
  company: CompanyWithStatus;
  insuranceId: string;
  clearError?: () => void;
  isError?: boolean;
}

const StatusIndicator: FC<{ status?: string }> = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'ACTIVE':
        return 'green.500';
      case 'DISABLED':
        return 'red.500';
      case 'UNDER_CONSTRUCTION':
        return 'orange.500';
      default:
        return 'gray.300';
    }
  };

  return (
    <Box
      width="8px"
      height="8px"
      borderRadius="50%"
      backgroundColor={getStatusColor()}
      flexShrink={0}
    />
  );
};

const FlowsConfigureCompanyItem: FC<Props> = ({
  insuranceId,
  company,
  clearError,
  isError,
}) => {
  const addCompany = useAnvaFlowsConfigureStore((state) => state.addCompany);
  const removeCompany = useAnvaFlowsConfigureStore(
    (state) => state.removeCompany,
  );
  const isSelected = useAnvaFlowsConfigureStore((state) =>
    state.companies[insuranceId]?.includes(company.id),
  );

  const isDisabled = company.status === 'DISABLED';
  const isUnderConstruction = company.status === 'UNDER_CONSTRUCTION';

  const handleSelect = () => {
    if (isDisabled) return;
    if (isError) clearError?.();
    if (isSelected) {
      removeCompany(insuranceId, company.id);
    } else {
      addCompany(insuranceId, company.id);
    }
  };

  const borderColor = isError
    ? 'danger.main'
    : isSelected
    ? 'primary.500'
    : 'gray.50';
  const textColor = isDisabled || isUnderConstruction ? 'gray.500' : undefined;
  const cardOpacity = isDisabled || isUnderConstruction ? 0.6 : 1;
  const cardCursor = isDisabled ? 'not-allowed' : 'pointer';

  return (
    <Card
      as="button"
      p="4"
      borderColor={borderColor}
      h="100%"
      onClick={handleSelect}
      cursor={cardCursor}
      transition="0.1s"
      opacity={cardOpacity}
      _hover={
        isDisabled
          ? {}
          : {
              borderColor: isSelected ? undefined : 'gray.100',
              boxShadow: 'level2',
            }
      }
    >
      <HStack spacing="4" alignItems="center" h="100%">
        <StatusIndicator status={company.status} />
        <Box
          flexGrow="1"
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          <Text color={textColor}>{company.name}</Text>
          {company.form && (
            <Text textStyle="smallBody" color="gray.500">
              {company.form_description}
            </Text>
          )}
        </Box>
        <TableCheckbox
          isChecked={isSelected}
          onChange={handleSelect}
          isDisabled={isDisabled}
        />
      </HStack>
    </Card>
  );
};

export default FlowsConfigureCompanyItem;
